/*
 * ContactCacheService
 *
 * Cache-first over IndexedDB (bManagedCacheStore / contacts, communicationChannels) for the
 * organization's contact list and communication channels. Data is written after a full GET; served
 * from cache on subsequent reads until invalidation.
 *
 * Records are keyed by `userId` + `organizationId` (LAX-9444). Invalidation is triggered by
 * (1) local updates via `invalidate*`, (2) `BroadcastChannel('contact-cache')` from peer tabs, and
 * (3) Deepstream: `ChannelService` events `contactsChanged` / `communicationChannelsChanged` broadcast
 * from the server after mutations. A `deepstreamReconnected` signal clears both caches to avoid
 * stale data after an offline period.
 */
angular.module('llax')
    .service('ContactCacheService', function($log, $q, $rootScope,
                                             ContactsResource, CommunicationChannelResource,
                                             LocalCacheService, ChannelService) {

        var _this = this;

        var CONTACTS_STORE = 'contacts';
        var CHANNELS_STORE = 'communicationChannels';

        var BROADCAST_CHANNEL_NAME = 'contact-cache';
        var INVALIDATE_CONTACTS_MSG = 'invalidate-contacts';
        var INVALIDATE_CHANNELS_MSG = 'invalidate-communication-channels';
        var INVALIDATE_ALL_MSG = 'invalidate-all';

        var broadcastChannel = null;

        function hasBroadcastChannel() {
            return typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function';
        }

        function getBroadcastChannel() {
            if (broadcastChannel) {
                return broadcastChannel;
            }
            if (!hasBroadcastChannel()) {
                return null;
            }
            broadcastChannel = new window.BroadcastChannel(BROADCAST_CHANNEL_NAME);
            broadcastChannel.onmessage = function(event) {
                var data = event && event.data ? event.data : {};
                handleBroadcast(data);
            };
            return broadcastChannel;
        }

        function postBroadcast(payload) {
            var channel = getBroadcastChannel();
            if (channel) {
                try {
                    channel.postMessage(payload);
                } catch (err) {
                    $log.debug('ContactCacheService: postMessage failed', err);
                }
            }
        }

        function handleBroadcast(payload) {
            if (!payload || !payload.type) {
                return;
            }
            switch (payload.type) {
                case INVALIDATE_CONTACTS_MSG:
                    deleteEntry(CONTACTS_STORE, payload.userId, payload.organizationId);
                    break;
                case INVALIDATE_CHANNELS_MSG:
                    deleteEntry(CHANNELS_STORE, payload.userId, payload.organizationId);
                    break;
                case INVALIDATE_ALL_MSG:
                    deleteEntry(CONTACTS_STORE, payload.userId, payload.organizationId);
                    deleteEntry(CHANNELS_STORE, payload.userId, payload.organizationId);
                    break;
                default:
                    break;
            }
            $rootScope.$broadcast('contactCacheInvalidated', payload);
        }

        function currentUserId() {
            return $rootScope && $rootScope.user ? $rootScope.user.userId : null;
        }

        function currentOrganizationId() {
            return $rootScope && $rootScope.organization ? $rootScope.organization.id : null;
        }

        function resolveScope(scope) {
            var userId = scope && scope.userId !== undefined ? scope.userId : currentUserId();
            var organizationId = scope && scope.organizationId !== undefined ?
                scope.organizationId :
                currentOrganizationId();
            if (_.isNil(userId) || _.isNil(organizationId)) {
                return null;
            }
            return {
                userId: String(userId),
                organizationId: String(organizationId)
            };
        }

        function entryKey(scope) {
            return [scope.userId, scope.organizationId];
        }

        function deleteEntry(storeName, userId, organizationId) {
            if (_.isNil(userId) || _.isNil(organizationId)) {
                return $q.when(null);
            }
            return LocalCacheService.deleteEntryAsync(storeName, [String(userId), String(organizationId)]);
        }

        function getCacheEntry(storeName, scope) {
            return LocalCacheService.getEntryAsync(storeName, entryKey(scope));
        }

        function putCacheEntry(storeName, scope, data) {
            var entry = {
                userId: scope.userId,
                organizationId: scope.organizationId,
                data: data
            };
            return LocalCacheService.putEntryAsync(storeName, entry);
        }

        // Server-side filter for the cached business-relationship list. Must stay in sync with
        // the React `useAllContactData` query so both clients share the same `contacts` IndexedDB
        // entry (same store, same `[userId, organizationId]` key). See LAX-9809.
        var CONTACTS_QUERY_PARAMS = {
            state: 'ESTABLISHED',
            role: ['DATA_RECIPIENT', 'DATA_SUPPLIER_AND_RECIPIENT']
        };

        function fetchList(resource, params) {
            var deferred = $q.defer();
            resource.query(params || {}, function(list) {
                deferred.resolve(list || []);
            }, function(err) {
                deferred.reject(err);
            });
            return deferred.promise;
        }

        function loadWithCache(options) {
            var storeName = options.storeName;
            var resource = options.resource;
            var params = options.params;

            var scope = resolveScope(options.scope);
            if (!scope) {
                $log.debug('ContactCacheService: missing user/organization scope; bypassing cache');
                return fetchList(resource, params);
            }

            return LocalCacheService.waitForDBInitialization().then(function() {
                return getCacheEntry(storeName, scope).then(function(cached) {
                    if (cached && _.isArray(cached.data)) {
                        return cached.data;
                    }
                    return fetchList(resource, params).then(function(list) {
                        return putCacheEntry(storeName, scope, list).then(function() {
                            return list;
                        });
                    });
                });
            });
        }

        this.getContacts = function(scope) {
            return loadWithCache({
                storeName: CONTACTS_STORE,
                resource: ContactsResource,
                params: CONTACTS_QUERY_PARAMS,
                scope: scope
            });
        };

        this.getCommunicationChannels = function(scope) {
            return loadWithCache({
                storeName: CHANNELS_STORE,
                resource: CommunicationChannelResource,
                scope: scope
            });
        };

        this.invalidateContacts = function(scope) {
            var resolved = resolveScope(scope);
            if (!resolved) {
                return $q.when(null);
            }
            return deleteEntry(CONTACTS_STORE, resolved.userId, resolved.organizationId)
                .finally(function() {
                    postBroadcast({
                        type: INVALIDATE_CONTACTS_MSG,
                        userId: resolved.userId,
                        organizationId: resolved.organizationId
                    });
                });
        };

        this.invalidateCommunicationChannels = function(scope) {
            var resolved = resolveScope(scope);
            if (!resolved) {
                return $q.when(null);
            }
            return deleteEntry(CHANNELS_STORE, resolved.userId, resolved.organizationId)
                .finally(function() {
                    postBroadcast({
                        type: INVALIDATE_CHANNELS_MSG,
                        userId: resolved.userId,
                        organizationId: resolved.organizationId
                    });
                });
        };

        this.invalidateAll = function(scope) {
            var resolved = resolveScope(scope);
            if (!resolved) {
                return $q.when(null);
            }
            return $q.all([
                deleteEntry(CONTACTS_STORE, resolved.userId, resolved.organizationId),
                deleteEntry(CHANNELS_STORE, resolved.userId, resolved.organizationId)
            ]).finally(function() {
                postBroadcast({
                    type: INVALIDATE_ALL_MSG,
                    userId: resolved.userId,
                    organizationId: resolved.organizationId
                });
            });
        };

        $rootScope.$on('channelMessageReceived', function(_event, eventData) {
            if (!eventData || !eventData.event) {
                return;
            }
            if (eventData.event === ChannelService.CONTACTS_CHANGED_EVENT) {
                _this.invalidateContacts();
            } else if (eventData.event === ChannelService.COMMUNICATION_CHANNELS_CHANGED_EVENT) {
                _this.invalidateCommunicationChannels();
            }
        });

        $rootScope.$on('deepstreamReconnected', function() {
            _this.invalidateAll();
        });

        getBroadcastChannel();
    });
